import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import "./generate-image.scss";
import "../../components/modal.scss";
import { useDialogo } from "../../components/use-dialogo";
import "./aviso-de-recusa.scss";
import { CircularProgress } from "@mui/material";
import { PopUpInfo } from "../../components/confirm-pop-up";
import AppUser, { MetadadosDaImagem } from "../../api/app-user.class";
import Imgen, { ConteudoRecusadoError, KleinEsgotadoError } from "../../api/imgen";
import Firebase from "../../api/firebase.class";
import { ElType } from "../../classes/comic.class";
import {
  NivelDoOlhar,
  DeBaixoParaCima,
  DeCimaParaBaixo,
  DeCimaDeTudo,
  Inclinado,
  BemDePerto,
  DaCinturaParaCima,
  CorpoInteiro,
} from "./desenhos-de-enquadramento";
import { reduzirParaReferencia } from "../../api/reduzir-imagem";
import { ESTILO_DA_CASA, ESTILOS_DA_TELA, estiloParaEnviar } from "../../api/estilos";
import {
  listarEstilos,
  EstiloComEspecificacao,
  pedirDescritivo,
  CampoDoDescritivo,
  DescritivoRecusadoError,
  listarEtiquetas,
  criarEtiqueta,
  EtiquetaRecusadaError,
  EtiquetaGlobal,
  GrupoDeEtiqueta,
} from "../../api/descritivo";
import { daPrateleira } from "./prateleira-da-imagem";

type PropsType = {
  close: (selectedImageUrl?: string, metadados?: MetadadosDaImagem) => void;
  user: AppUser;
  setPopUpInfo: Dispatch<SetStateAction<PopUpInfo | null>>;
  category?: ElType | null;
};

/**
 * OS ÂNGULOS DE CÂMERA, EM PORTUGUÊS DE CRIANÇA — `T8.16` e `T8.34`.
 *
 * O achado, na letra: *"O modal de geração usa termos de cinema como 'Contra-plongée', 'Plongée' e
 * 'Ângulo holandês'."* A sugestão traz a tradução pronta, e ela é a régua deste conserto:
 *
 *     Contra-plongée    ->  De baixo para cima
 *     Plongée           ->  De cima para baixo
 *     Ângulo holandês   ->  Inclinado
 *
 * ── O QUE MUDOU E O QUE NÃO PODIA MUDAR ──────────────────────────────────────────────────────
 *
 * O `rotulo` é o que a criança lê. A `frase` é o que vai para o prompt do gerador, e ela **não foi
 * tocada**: são frases testadas, e trocá-las mudaria o desenho que sai. Rótulo e prompt são coisas
 * diferentes, e confundi-los seria consertar a tela quebrando o resultado.
 *
 * TRADUZI TAMBÉM OS QUE O RELATÓRIO NÃO NOMEOU — "Close-up", "Plano médio", "Plano aberto" — e não
 * é ampliação de escopo: são o mesmo defeito (jargão) e dois deles são inglês, o que já viola uma
 * decisão medida deste épico (`T1.32`, a varredura de português). Deixar "Close-up" ao lado de "De
 * baixo para cima" ensinaria que metade da tela é para quem sabe cinema.
 *
 * E CADA UM TEM UM MINI-DESENHO, que é a outra metade do `T8.34`. "De baixo para cima" ainda é
 * ambíguo por escrito — baixo do quê? —, e o desenho responde em um olhar. A gramática dos oito é
 * uma só: o retângulo é o personagem, o ponto cheio é de onde se olha. Ver
 * `desenhos-de-enquadramento.tsx`.
 */
const CAMERA_ANGLE_PRESETS: {
  rotulo: string;
  phrase: string;
  Desenho: () => JSX.Element;
}[] = [
  {
    rotulo: "No nível dos olhos",
    phrase: "câmera ao nível dos olhos",
    Desenho: NivelDoOlhar,
  },
  {
    rotulo: "De baixo para cima",
    phrase: "ângulo de câmera baixo, visto de baixo para cima",
    Desenho: DeBaixoParaCima,
  },
  {
    rotulo: "De cima para baixo",
    phrase: "ângulo de câmera alto, visto de cima para baixo",
    Desenho: DeCimaParaBaixo,
  },
  {
    rotulo: "De cima de tudo",
    phrase: "vista aérea de cima (bird's eye view)",
    Desenho: DeCimaDeTudo,
  },
  {
    rotulo: "Inclinado",
    phrase: "ângulo holandês inclinado",
    Desenho: Inclinado,
  },
  {
    rotulo: "Bem de perto, no rosto",
    phrase: "close-up no rosto",
    Desenho: BemDePerto,
  },
  {
    rotulo: "Da cintura para cima",
    phrase: "plano médio, da cintura para cima",
    Desenho: DaCinturaParaCima,
  },
  {
    rotulo: "O corpo inteiro",
    phrase: "plano aberto, corpo inteiro",
    Desenho: CorpoInteiro,
  },
];

const POSE_PRESETS: { label: string; phrase: string }[] = [
  { label: "De frente", phrase: "de frente para a câmera" },
  { label: "Perfil", phrase: "de perfil" },
  // "Três quartos" é a mesma doença dos ângulos: é jargão de quem já sabe. O que a criança vê é
  // o personagem meio virado, e é isso que o rótulo passa a dizer. A `phrase` não muda.
  { label: "Meio de lado", phrase: "vista em três quartos (3/4)" },
  { label: "De costas", phrase: "visto de costas" },
  { label: "Corpo inteiro", phrase: "corpo inteiro visível" },
  { label: "Em pé", phrase: "em pé" },
  { label: "Sentado", phrase: "sentado" },
  { label: "Pose de ação", phrase: "em pose de ação dinâmica" },
];

/**
 * O NOME EM PORTUGUÊS DA PRATELEIRA EM QUE O DESENHO VAI CAIR — `T8.32`.
 *
 * A dica do relatório é *"clique na imagem gerada para guardá-la"*, e dizer **onde** ela é guardada
 * custa três palavras. Sem isso a pessoa clica, a tela fecha e o desenho parece ter sumido: ele está
 * na prateleira, que é a que estava aberta quando ela mandou gerar.
 *
 * O padrão é Cenários porque é o padrão do `saveImg` quando nenhuma prateleira está aberta — a tela
 * tem de dizer a verdade sobre o código, e não a verdade que seria mais bonita.
 */
const NOME_DA_PRATELEIRA: Record<string, string> = {
  characters: "Personagens",
  scenarios: "Cenários",
  figures: "Figuras",
  textBubbles: "Textos",
  rectangles: "Figuras",
};

/** T24 · o título e o botão dizem o que a prateleira gera. O operador: «deveria ser gerar personagem». */
export const O_QUE_GERA: Record<string, string> = {
  characters: "Gerar personagem",
  scenarios: "Gerar cenário",
  figures: "Gerar figura",
};

interface Etiqueta {
  chave: string;
  rotulo: string;
  /** O que vai ao pedido — para as da casa, o próprio texto. */
  frase: string;
  grupo: GrupoDeEtiqueta;
  Desenho?: () => JSX.Element;
}

const ETIQUETAS_FIXAS: Etiqueta[] = [
  ...CAMERA_ANGLE_PRESETS.map((p) => ({ chave: `a:${p.rotulo}`, rotulo: p.rotulo, frase: p.phrase, grupo: "angulo" as const, Desenho: p.Desenho })),
  ...POSE_PRESETS.map((p) => ({ chave: `p:${p.label}`, rotulo: p.label, frase: p.phrase, grupo: "pose" as const })),
];

const NOME_DO_GRUPO: Record<GrupoDeEtiqueta, string> = {
  angulo: "Ângulo e enquadramento",
  enquadramento: "Ângulo e enquadramento",
  pose: "Pose",
  outra: "Etiquetas do HQ-Lab",
};

/**
 * A JANELA DE GERAR — refeita no T24 (13/10/2026).
 *
 * O operador, na letra: «esse quadrado, que inclusive está muito espremido, poderia ser uma coisa um
 * pouquinho mais bem pensada»; «em vez de gerar inicialmente a imagem, ele deveria gerar para mim um
 * descritivo (...) e aí eu posso editar esse descritivo»; «quando você clica numa tag, aquilo já
 * automaticamente entra no caixa de descritivo e não deveria (...) e aí no preview aparece»; «criar
 * uma tag nova ali e salvar essa tag»; e «vem estouradíssimo (...) no tamanho das coisas».
 *
 * A ordem da janela é a ordem do trabalho: (1) o que você quer — descrição, estilo, etiquetas,
 * referência; (2) o descritivo, que o modelo de texto escreve e a pessoa edita; (3) a prévia do que
 * vai ao desenhista; (4) o desenho, inteiro e num tamanho de conferir, com os botões de guardar,
 * tirar o fundo e separar em camadas — e um aviso do que cada um fez.
 */
type Tela = "pedido" | "descritivo" | "desenhos";

/** O estilo "da referência": com um personagem escolhido, o desenho pode seguir o traço dele. */
const ESTILO_DA_REFERENCIA = "referencia";

/**
 * A JANELA DE GERAR, EM TRÊS TELAS — T25 (13/10/2026).
 *
 * O operador, sobre a do T24: «extremamente mal feita (...) em termos de espaçamento, linha (...)
 * os botões estão completamente fora do padrão estético do HQLab»; «Tem que dar scroll, que é uma
 * coisa que eu não gostaria»; «o descritivo tem que ser uma segunda tela»; «Deveria ser quase uma
 * estrutura de JSON com as características no detalhe»; «Quando eu seleciono uma imagem (...) o
 * estilo me obriga a seguir esse massinha 3D»; e «se eu clico fora aqui, fecha (...) só quando eu
 * apertar no X».
 *
 * Então: uma tela por etapa, cada uma do tamanho da janela (sem rolagem); botões e campos com a
 * receita do resto do produto (borda e cor dos tokens, botão em pílula); o descritivo em campos
 * editáveis; o estilo sempre escolhível; e a janela só fecha pelo X — ou pelo Esc, que faz o mesmo
 * que o X —, avisando quando há desenho em andamento ou desenho não guardado.
 */
function GenerateImage({ close, user, setPopUpInfo, category }: PropsType) {
  const titulo = O_QUE_GERA[category ?? ""] ?? "Gerar imagem";
  const [tela, setTela] = useState<Tela>("pedido");
  const [descricao, setDescricao] = useState("");
  const [estilo, setEstilo] = useState(ESTILO_DA_CASA);
  const [estilos, setEstilos] = useState<EstiloComEspecificacao[]>(
    ESTILOS_DA_TELA.map((e) => ({ ...e, personagem: "", cenario: "" }))
  );
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [daCasa, setDaCasa] = useState<EtiquetaGlobal[]>([]);
  const [criandoEtiqueta, setCriandoEtiqueta] = useState<string | null>(null);
  const [salvandoEtiqueta, setSalvandoEtiqueta] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [descritivoDaReferencia, setDescritivoDaReferencia] = useState<string | undefined>(undefined);
  const [campos, setCampos] = useState<CampoDoDescritivo[]>([]);
  const [pedindoDescritivo, setPedindoDescritivo] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [guardadas, setGuardadas] = useState<Record<string, true>>({});
  const [recusa, setRecusa] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupadaEm, setOcupadaEm] = useState<{ indice: number; acao: "fundo" | "camadas" | "quadros" } | null>(null);
  const [feito, setFeito] = useState<Record<string, true>>({});
  const [perguntaDeFechar, setPerguntaDeFechar] = useState(false);
  const [guardandoTudo, setGuardandoTudo] = useState(false);

  const personagensSalvos = daPrateleira(user.Imgs() || {}, "characters").slice(-5).reverse();

  useEffect(() => {
    let vivo = true;
    listarEstilos().then((l) => vivo && setEstilos(l));
    listarEtiquetas().then((l) => vivo && setDaCasa(l));
    return () => {
      vivo = false;
    };
  }, []);

  const todas: Etiqueta[] = useMemo(
    () => [
      ...(category === "characters" ? ETIQUETAS_FIXAS : ETIQUETAS_FIXAS.filter((e) => e.grupo === "angulo")),
      ...daCasa.map((e) => ({ chave: `g:${e.id}`, rotulo: e.texto, frase: e.texto, grupo: e.grupo })),
    ],
    [category, daCasa]
  );
  const escolhidas = todas.filter((e) => marcadas.has(e.chave));
  const estiloAtual = estilos.find((e) => e.tag === estilo);
  const especificacao = category === "scenarios" ? estiloAtual?.cenario : estiloAtual?.personagem;
  /** Com referência e "o da referência", nenhum estilo vai: o traço sai da imagem. */
  const estiloEnviado = estilo === ESTILO_DA_REFERENCIA ? undefined : estiloParaEnviar(estilo);
  const ocupada = isGenerating || pedindoDescritivo || ocupadaEm !== null || guardandoTudo;
  const naoGuardadas = generatedImages.filter((u) => !guardadas[u]);

  function alternar(chave: string) {
    setMarcadas((atual) => {
      const nova = new Set(atual);
      if (nova.has(chave)) nova.delete(chave);
      else nova.add(chave);
      return nova;
    });
  }

  function escolherReferencia(url: string, descritivoSalvo?: string) {
    const sai = imageUrl === url;
    setImageUrl(sai ? "" : url);
    setDescritivoDaReferencia(sai ? undefined : descritivoSalvo);
    // Escolher a referência põe o estilo "da referência" — e qualquer outro continua a um clique.
    if (!sai && estilo === ESTILO_DA_CASA) setEstilo(ESTILO_DA_REFERENCIA);
    if (sai && estilo === ESTILO_DA_REFERENCIA) setEstilo(ESTILO_DA_CASA);
  }

  async function salvarEtiqueta() {
    const uid = user.Id();
    const texto = (criandoEtiqueta ?? "").trim();
    if (!uid || texto.length < 2) return;
    setSalvandoEtiqueta(true);
    setRecusa(null);
    try {
      const e = await criarEtiqueta(texto, category === "characters" ? "pose" : "outra", uid);
      setDaCasa((l) => [...l, e].sort((a, b) => a.texto.localeCompare(b.texto, "pt-BR")));
      setMarcadas((m) => new Set(m).add(`g:${e.id}`));
      setCriandoEtiqueta(null);
    } catch (err) {
      setRecusa(err instanceof EtiquetaRecusadaError ? err.message : "Não deu para salvar a etiqueta agora. Tente de novo.");
    } finally {
      setSalvandoEtiqueta(false);
    }
  }

  async function montarDescritivo() {
    if (!descricao.trim() || pedindoDescritivo) return;
    setPedindoDescritivo(true);
    setRecusa(null);
    setTela("descritivo");
    try {
      const d = await pedirDescritivo({
        descricao,
        tipo: category,
        etiquetas: escolhidas.map((e) => e.rotulo),
        estiloRotulo: estilo === ESTILO_DA_REFERENCIA ? "o mesmo da imagem de referência" : estiloAtual?.rotulo,
        temReferencia: !!imageUrl,
        descritivoDaReferencia,
      });
      setCampos(d.campos);
    } catch (err) {
      setRecusa(err instanceof DescritivoRecusadoError ? err.message : "Não deu para montar o descritivo agora. Tente de novo daqui a pouco.");
    } finally {
      setPedindoDescritivo(false);
    }
  }

  const descritivoTexto = campos
    .filter((c) => c.valor.trim())
    .map((c) => `${c.rotulo}: ${c.valor.trim()}`)
    .join("\n");
  /** O que vai ao desenhista: os campos do descritivo e as etiquetas marcadas. O estilo vai à parte. */
  const pedidoFinal = [descritivoTexto, escolhidas.length ? `Escolhas: ${escolhidas.map((e) => e.frase).join("; ")}` : ""]
    .filter(Boolean)
    .join("\n");

  async function desenhar() {
    if (isGenerating || !descritivoTexto) return;
    setTela("desenhos");
    setIsGenerating(true);
    setRecusa(null);
    setAviso(null);
    try {
      let result: { images: string[]; provider: string };
      try {
        result = imageUrl
          ? await Imgen.generateComReferencia(pedidoFinal, await reduzirParaReferencia(imageUrl, 1024, true), undefined, "continuar", category, undefined, estiloEnviado)
          : await Imgen.generateComReferencia(pedidoFinal, "", undefined, "texto", category, undefined, estiloEnviado);
      } catch (erro) {
        // T22.2 · só com TODAS as contas do klein esgotadas o desenho vai pelo caminho antigo, e diz.
        if (!(erro instanceof KleinEsgotadoError)) throw erro;
        const curto = [descricao.trim(), ...escolhidas.map((e) => e.frase)].join(", ");
        result = await Imgen.generateImages(curto, undefined, undefined, category, estiloEnviado);
        setAviso(
          "Este desenho foi feito por outro desenhista: o principal já usou todo o limite de hoje. " +
            (imageUrl ? "Por isso a imagem de referência não pôde ser usada. " : "") +
            "O limite volta às 21h."
        );
      }
      setGeneratedImages((anteriores) => [...result.images, ...anteriores]);
      const uid = user.Id();
      if (uid) {
        Firebase.pushDocument(`users/${uid}/generatedImages`, {
          prompt: descricao,
          createdAt: Date.now(),
          ids: result.images,
          status: "ok",
        }).catch((err) => console.error("Falha ao registrar no histórico:", err));
      }
    } catch (err) {
      if (err instanceof ConteudoRecusadoError) {
        setRecusa(err.message);
        const uid = user.Id();
        if (uid) {
          Firebase.pushDocument(`users/${uid}/generatedImages`, {
            prompt: descricao,
            createdAt: Date.now(),
            ids: [],
            status: "blocked",
          }).catch((e) => console.error("Falha ao registrar recusa:", e));
        }
      } else {
        console.error(err);
        setRecusa("Não deu para desenhar agora. Tente de novo em instantes.");
      }
    } finally {
      setIsGenerating(false);
    }
  }

  /** T24.13 · o que entrou no desenho vai junto quando ele é guardado. */
  const metadados: MetadadosDaImagem = {
    descricao: descricao.trim() || undefined,
    descritivo: descritivoTexto || undefined,
    etiquetas: escolhidas.map((e) => e.rotulo),
    estilo,
  };

  async function semFundo(url: string): Promise<string> {
    const r = await Imgen.generateComReferencia("", await reduzirParaReferencia(url, 1024), undefined, "isolar-personagem");
    return r.images[0];
  }

  async function tirarFundo(i: number, url: string) {
    setOcupadaEm({ indice: i, acao: "fundo" });
    setRecusa(null);
    try {
      const nova = await semFundo(url);
      setGeneratedImages((atuais) => atuais.map((u, j) => (j === i ? nova : u)));
      setFeito((f) => ({ ...f, [`fundo-${i}`]: true }));
      setAviso("Fundo tirado. Guarde o desenho para usá-lo no quadro.");
    } catch (e) {
      console.error("[tirar-fundo]", e);
      setRecusa("Não deu para tirar o fundo agora. O desenho continua aqui do jeito que veio.");
    } finally {
      setOcupadaEm(null);
    }
  }

  async function guardarCamadas(url: string): Promise<{ total: number; personagens: number; cenarios: number }> {
    const partes = await Imgen.generateComReferencia("", await reduzirParaReferencia(url, 1024), undefined, "camadas");
    const nomes = partes.nomes ?? [];
    let personagens = 0;
    let cenarios = 0;
    for (let k = 0; k < partes.images.length; k++) {
      const ehPersonagem = (nomes[k] ?? "").startsWith("personagem");
      await user.saveImg(partes.images[k], ehPersonagem ? "characters" : "scenarios", metadados);
      if (ehPersonagem) personagens++;
      else cenarios++;
    }
    return { total: partes.images.length, personagens, cenarios };
  }

  async function separarEmCamadas(i: number, url: string) {
    setOcupadaEm({ indice: i, acao: "camadas" });
    setRecusa(null);
    try {
      const r = await guardarCamadas(url);
      setFeito((f) => ({ ...f, [`camadas-${i}`]: true }));
      setGuardadas((g) => ({ ...g, [url]: true }));
      // T24.17 · o aviso: o que foi salvo, quanto, e onde achar.
      setAviso(`Pronto: ${r.total} camadas guardadas — ${r.personagens} em Personagens e ${r.cenarios} em Cenários.`);
    } catch (e) {
      console.error("[separar-camadas]", e);
      setRecusa(
        e instanceof ConteudoRecusadoError
          ? "A verificação de segurança recusou esta imagem. Ela continua aqui inteira."
          : "Não deu para separar esta imagem agora. Ela continua aqui inteira — tente de novo daqui a pouco."
      );
    } finally {
      setOcupadaEm(null);
    }
  }

  /**
   * T21.4 · A PÁGINA EM GRADE VIRA QUADROS. O operador: «criar um step de quando gerar uma imagem
   * como imagem inteira (...) fazer um processo de decupagem». Recorte pelas calhas, no servidor,
   * sem IA e sem crédito; cada quadro vai para Cenários.
   */
  async function separarOsQuadros(i: number, url: string) {
    setOcupadaEm({ indice: i, acao: "quadros" });
    setRecusa(null);
    try {
      const r = await Imgen.generateComReferencia("", await reduzirParaReferencia(url, 1024), undefined, "quadros");
      if (r.images.length < 2) {
        setAviso("Este desenho não é uma página com vários quadros — ele já é um quadro só.");
        return;
      }
      for (const q of r.images) await user.saveImg(q, "scenarios", metadados);
      setFeito((f) => ({ ...f, [`quadros-${i}`]: true }));
      setAviso(`Pronto: ${r.images.length} quadros guardados em Cenários, um por imagem.`);
    } catch (e) {
      console.error("[separar-quadros]", e);
      setRecusa("Não deu para separar os quadros agora. O desenho continua aqui inteiro.");
    } finally {
      setOcupadaEm(null);
    }
  }

  async function guardar(url: string) {
    await user.saveImg(url, (category ?? "scenarios") as ElType, metadados);
    setGuardadas((g) => ({ ...g, [url]: true }));
    setAviso(`Guardado em ${NOME_DA_PRATELEIRA[category ?? "scenarios"]}.`);
  }

  /** T25.11 · fechar com desenho não guardado: guardar já, do jeito escolhido, e só então sair. */
  async function guardarTudoEFechar(jeito: "como-esta" | "sem-fundo" | "camadas") {
    setGuardandoTudo(true);
    setRecusa(null);
    try {
      for (const url of naoGuardadas) {
        if (jeito === "camadas") await guardarCamadas(url);
        else await user.saveImg(jeito === "sem-fundo" ? await semFundo(url) : url, (category ?? "scenarios") as ElType, metadados);
      }
      close();
    } catch (e) {
      console.error("[guardar-tudo]", e);
      setRecusa("Não deu para guardar tudo agora. Os desenhos continuam aqui.");
      setGuardandoTudo(false);
    }
  }

  /** T25.10 · a janela só sai por aqui — o X, ou o Esc, que faz o mesmo. */
  function pedirParaFechar() {
    if (ocupada || naoGuardadas.length) setPerguntaDeFechar(true);
    else close();
  }

  useDialogo(pedirParaFechar);

  const grupos = (["angulo", "pose", "outra"] as GrupoDeEtiqueta[])
    .map((g) => ({ g, itens: todas.filter((e) => (g === "angulo" ? e.grupo === "angulo" || e.grupo === "enquadramento" : e.grupo === g)) }))
    .filter((x) => x.itens.length || x.g === "outra");

  const PASSOS: { id: Tela; nome: string }[] = [
    { id: "pedido", nome: "Pedido" },
    { id: "descritivo", nome: "Descritivo" },
    { id: "desenhos", nome: "Desenhos" },
  ];

  return (
    <div className="generate-image-bg hq-modal-fundo">
      <div className="generate-image-container gerar-t25 hq-modal-caixa" role="dialog" aria-modal="true" aria-label={titulo}>
        <div className="header hq-modal-topo gerar-topo">
          <h2 className="gerar-titulo">{titulo}</h2>
          <ol className="gerar-passos" aria-label="Etapas">
            {PASSOS.map((p, i) => (
              <li key={p.id} aria-current={tela === p.id ? "step" : undefined} className={tela === p.id ? "atual" : ""}>
                <span className="gerar-passo-numero">{i + 1}</span> {p.nome}
              </li>
            ))}
          </ol>
          <button
            type="button"
            className="hq-modal-fechar"
            aria-label="Fechar e voltar ao editor"
            title="Fechar e voltar ao editor"
            onClick={pedirParaFechar}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        <div className="hq-modal-corpo gerar-corpo">
          {(recusa || aviso) && (
            <div className="gerar-faixa" aria-live="polite">
              {recusa && (
                <p className="gerar-recusa" role="alert">
                  {recusa}
                </p>
              )}
              {aviso && (
                <p className="gerar-aviso" role="status">
                  {aviso}
                </p>
              )}
            </div>
          )}

          {tela === "pedido" && (
            <div className="gerar-tela gerar-tela-pedido">
              <section className="gerar-coluna" aria-label="O que você quer">
                <div className="gerar-campo">
                  <label htmlFor="imagePrompt">O que você quer desenhar</label>
                  <textarea
                    id="imagePrompt"
                    rows={3}
                    placeholder="Ex.: um menino de 9 anos que gosta de jogar bola, de boné vermelho com uma estrela"
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                  />
                </div>

                <fieldset className="gerar-campo">
                  <legend>Estilo do desenho</legend>
                  <div className="gerar-chips">
                    {imageUrl && (
                      <button type="button" className="gerar-chip" aria-pressed={estilo === ESTILO_DA_REFERENCIA} onClick={() => setEstilo(ESTILO_DA_REFERENCIA)}>
                        O da referência
                      </button>
                    )}
                    {estilos.map((e) => (
                      <button key={e.tag} type="button" className="gerar-chip" aria-pressed={estilo === e.tag} onClick={() => setEstilo(e.tag)}>
                        {e.rotulo}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="gerar-campo">
                  <legend>{category === "characters" ? "Mudar a pose de um personagem seu (opcional)" : "Imagem de referência (opcional)"}</legend>
                  <div className="gerar-referencias">
                    {personagensSalvos.map(([id, info]) => (
                      <button
                        key={id}
                        type="button"
                        className="gerar-referencia"
                        aria-pressed={imageUrl === info.url}
                        aria-label="Usar este personagem como referência"
                        onClick={() => escolherReferencia(info.url, info.descritivo)}
                      >
                        <img src={info.url} alt="" width={64} height={64} />
                      </button>
                    ))}
                    <label className="gerar-referencia gerar-enviar" title="Enviar uma imagem do computador (PNG, JPG ou WEBP, até 4 MB)">
                      <span aria-hidden="true">＋</span>
                      <span className="gerar-enviar-texto">Enviar</span>
                      <input
                        id="imagemDeReferencia"
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="visualmente-escondido"
                        onChange={(event) => {
                          const arquivo = event.target.files?.[0];
                          if (!arquivo) return;
                          if (arquivo.size > 4 * 1024 * 1024) {
                            setPopUpInfo({
                              text: `Esta imagem tem ${Math.round(arquivo.size / 1024 / 1024)} MB e o limite é 4 MB. Escolha uma menor.`,
                              type: "alert",
                              close: () => setPopUpInfo(null),
                              confirm: () => setPopUpInfo(null),
                            });
                            event.target.value = "";
                            return;
                          }
                          const leitor = new FileReader();
                          leitor.onload = () => escolherReferencia(String(leitor.result || ""));
                          leitor.readAsDataURL(arquivo);
                        }}
                      />
                    </label>
                  </div>
                </fieldset>
              </section>

              <section className="gerar-coluna" aria-label="Etiquetas">
                {grupos.map(({ g, itens }) => (
                  <fieldset key={g} className="gerar-campo">
                    <legend>{NOME_DO_GRUPO[g]}</legend>
                    <div className="gerar-chips">
                      {itens.map((e) => (
                        <button
                          key={e.chave}
                          type="button"
                          className={`gerar-chip preset-chip${e.Desenho ? " gerar-chip-com-desenho" : ""}`}
                          aria-pressed={marcadas.has(e.chave)}
                          onClick={() => alternar(e.chave)}
                          title={e.rotulo}
                        >
                          {e.Desenho && <e.Desenho />}
                          <span>{e.rotulo}</span>
                        </button>
                      ))}
                      {g === "outra" &&
                        (criandoEtiqueta === null ? (
                          <button type="button" className="gerar-chip gerar-chip-nova" onClick={() => setCriandoEtiqueta("")} disabled={!user.Id()}>
                            ＋ Nova etiqueta
                          </button>
                        ) : (
                          <span className="gerar-nova-etiqueta">
                            <input
                              id="novaEtiqueta"
                              aria-label="Texto da etiqueta nova (vale para todo o HQ-Lab)"
                              maxLength={40}
                              autoFocus
                              placeholder="Ex.: segurando uma bola"
                              value={criandoEtiqueta}
                              onChange={(e) => setCriandoEtiqueta(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && salvarEtiqueta()}
                            />
                            <button type="button" className="gerar-chip" disabled={salvandoEtiqueta || criandoEtiqueta.trim().length < 2} onClick={salvarEtiqueta}>
                              {salvandoEtiqueta ? "Salvando…" : "Salvar"}
                            </button>
                            <button type="button" className="gerar-chip" onClick={() => setCriandoEtiqueta(null)}>
                              Cancelar
                            </button>
                          </span>
                        ))}
                    </div>
                  </fieldset>
                ))}
              </section>
            </div>
          )}

          {tela === "descritivo" && (
            <div className="gerar-tela gerar-tela-descritivo">
              <section className="gerar-campos" aria-label="O descritivo" aria-busy={pedindoDescritivo}>
                {pedindoDescritivo && <p className="gerar-carregando">Montando o descritivo…</p>}
                {!pedindoDescritivo &&
                  campos.map((c, i) => (
                    <div key={c.chave} className="gerar-campo gerar-campo-curto">
                      <label htmlFor={`campo-${c.chave}`}>{c.rotulo}</label>
                      <textarea
                        id={`campo-${c.chave}`}
                        rows={3}
                        value={c.valor}
                        onChange={(e) => setCampos((l) => l.map((x, j) => (j === i ? { ...x, valor: e.target.value } : x)))}
                      />
                    </div>
                  ))}
              </section>
              <aside className="gerar-previa" aria-label="O que vai ao desenhista">
                <h3>O que vai ao desenhista</h3>
                <p>
                  <strong>Pedido:</strong> {descricao.trim()}
                </p>
                <p>
                  <strong>Estilo:</strong>{" "}
                  {estilo === ESTILO_DA_REFERENCIA ? "o da imagem de referência" : estiloAtual?.rotulo}
                  {estilo !== ESTILO_DA_REFERENCIA && especificacao ? <span className="gerar-especificacao"> — {especificacao}</span> : null}
                </p>
                {escolhidas.length > 0 && (
                  <p>
                    <strong>Escolhas:</strong> {escolhidas.map((e) => e.rotulo).join(", ")}
                  </p>
                )}
                {imageUrl && (
                  <div className="gerar-previa-referencia">
                    <img src={imageUrl} alt="Personagem de referência" />
                    <span>O mesmo personagem desta imagem.</span>
                  </div>
                )}
              </aside>
            </div>
          )}

          {tela === "desenhos" && (
            <div className="gerar-tela gerar-tela-desenhos">
              <div className="gerar-grade" aria-label="Desenhos gerados">
                {isGenerating && (
                  <div className="gerar-cartao gerar-cartao-esperando" aria-live="polite">
                    <CircularProgress size={40} />
                    <span>Desenhando…</span>
                  </div>
                )}
                {generatedImages.map((url, i) => (
                  <figure key={url.slice(-40) + i} className="opcao-de-imagem gerar-cartao">
                    <img src={url} alt={`Desenho gerado ${i + 1}`} className="gerar-miniatura" draggable={false} />
                    <figcaption>
                      <button type="button" className="gerar-botao gerar-botao-principal" disabled={!!guardadas[url]} onClick={() => guardar(url)}>
                        {guardadas[url] ? "Guardado ✓" : `Guardar em ${NOME_DA_PRATELEIRA[category ?? "scenarios"]}`}
                      </button>
                      <div className="gerar-linha">
                        <button type="button" className="gerar-botao botao-tirar-fundo" disabled={ocupadaEm !== null || !!feito[`fundo-${i}`]} onClick={() => tirarFundo(i, url)}>
                          {ocupadaEm?.indice === i && ocupadaEm.acao === "fundo" ? "Tirando…" : feito[`fundo-${i}`] ? "Sem fundo ✓" : "Tirar o fundo"}
                        </button>
                        <button type="button" className="gerar-botao" disabled={ocupadaEm !== null || !!feito[`quadros-${i}`] || !user.Id()} onClick={() => separarOsQuadros(i, url)}>
                          {ocupadaEm?.indice === i && ocupadaEm.acao === "quadros" ? "Separando…" : feito[`quadros-${i}`] ? "Quadros ✓" : "Separar os quadros"}
                        </button>
                        <button type="button" className="gerar-botao botao-separar-partes" disabled={ocupadaEm !== null || !!feito[`camadas-${i}`] || !user.Id()} onClick={() => separarEmCamadas(i, url)}>
                          {ocupadaEm?.indice === i && ocupadaEm.acao === "camadas" ? "Separando…" : feito[`camadas-${i}`] ? "Camadas ✓" : "Separar em camadas"}
                        </button>
                      </div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="gerar-rodape">
          {tela === "pedido" && (
            <button type="button" className="gerar-botao gerar-botao-principal" disabled={!descricao.trim() || pedindoDescritivo} onClick={montarDescritivo}>
              Montar o descritivo →
            </button>
          )}
          {tela === "descritivo" && (
            <>
              <button type="button" className="gerar-botao" onClick={() => setTela("pedido")} disabled={pedindoDescritivo}>
                ← Voltar ao pedido
              </button>
              <button type="button" className="gerar-botao" onClick={montarDescritivo} disabled={pedindoDescritivo}>
                Montar outro descritivo
              </button>
              <button type="button" className="gerar-botao gerar-botao-principal submit-button" disabled={pedindoDescritivo || !descritivoTexto} onClick={desenhar}>
                {titulo} →
              </button>
            </>
          )}
          {tela === "desenhos" && (
            <>
              <button type="button" className="gerar-botao" onClick={() => setTela("descritivo")} disabled={isGenerating}>
                ← Voltar ao descritivo
              </button>
              <button type="button" className="gerar-botao gerar-botao-principal" onClick={desenhar} disabled={isGenerating || !descritivoTexto}>
                Desenhar mais um
              </button>
            </>
          )}
        </div>

        {perguntaDeFechar && (
          <div className="gerar-pergunta" role="alertdialog" aria-modal="true" aria-labelledby="gerar-pergunta-titulo">
            <div className="gerar-pergunta-caixa">
              {ocupada ? (
                <>
                  <h3 id="gerar-pergunta-titulo">{guardandoTudo ? "Guardando os desenhos…" : "Ainda estou trabalhando"}</h3>
                  <p>
                    {isGenerating
                      ? "Um desenho está sendo feito agora. Se fechar, ele se perde."
                      : pedindoDescritivo
                        ? "O descritivo está sendo montado. Se fechar, ele se perde."
                        : guardandoTudo
                          ? "Espere terminar de guardar."
                          : "Estou tirando o fundo ou separando as camadas. Se fechar, isso se perde."}
                  </p>
                  <div className="gerar-linha">
                    <button type="button" className="gerar-botao gerar-botao-principal" onClick={() => setPerguntaDeFechar(false)} disabled={guardandoTudo}>
                      Continuar aqui
                    </button>
                    <button type="button" className="gerar-botao gerar-botao-perigo" onClick={() => close()} disabled={guardandoTudo}>
                      Fechar e interromper
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 id="gerar-pergunta-titulo">
                    {naoGuardadas.length === 1 ? "Há 1 desenho que você não guardou" : `Há ${naoGuardadas.length} desenhos que você não guardou`}
                  </h3>
                  <p>Quer guardar antes de fechar?</p>
                  <div className="gerar-linha">
                    <button type="button" className="gerar-botao gerar-botao-principal" onClick={() => guardarTudoEFechar("como-esta")}>
                      Guardar como está
                    </button>
                    <button type="button" className="gerar-botao" onClick={() => guardarTudoEFechar("sem-fundo")}>
                      Guardar sem fundo
                    </button>
                    {user.Id() && (
                      <button type="button" className="gerar-botao" onClick={() => guardarTudoEFechar("camadas")}>
                        Guardar em camadas
                      </button>
                    )}
                  </div>
                  <div className="gerar-linha">
                    <button type="button" className="gerar-botao" onClick={() => setPerguntaDeFechar(false)}>
                      Continuar aqui
                    </button>
                    <button type="button" className="gerar-botao gerar-botao-perigo" onClick={() => close()}>
                      Fechar sem guardar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GenerateImage;
